import * as Dialog from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import type { ReactNode } from "react";
import { cn } from "@/lib/cn";

// Tailwind's `sr-only` utility — clip the text visually while keeping it
// reachable to screen readers. Used to satisfy Radix's Title/Description
// requirement when the caller didn't pass one.
const SR_ONLY = "sr-only";

type ModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title?: string;
  description?: string;
  children: ReactNode;
  size?: "sm" | "md" | "lg" | "xl";
  className?: string;
};

const SIZES = {
  sm: "max-w-sm",
  md: "max-w-md",
  lg: "max-w-lg",
  xl: "max-w-2xl",
} as const;

export function Modal({
  open,
  onOpenChange,
  title,
  description,
  children,
  size = "md",
  className,
}: ModalProps) {
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay
          className={cn(
            "fixed inset-0 z-40 bg-black/60 backdrop-blur-sm",
            "data-[state=open]:animate-in data-[state=closed]:animate-out",
            "data-[state=open]:fade-in data-[state=closed]:fade-out",
          )}
        />
        <Dialog.Content
          className={cn(
            "fixed left-1/2 top-1/2 z-50 -translate-x-1/2 -translate-y-1/2",
            "w-[92vw] rounded-2xl border border-stroke-strong bg-surface",
            "shadow-[0_24px_48px_-20px_#000,inset_0_1px_0_rgba(255,255,255,0.04)]",
            "max-h-[88vh] overflow-y-auto p-4 sm:p-6 focus:outline-none",
            "data-[state=open]:animate-in data-[state=closed]:animate-out",
            SIZES[size],
            className,
          )}
        >
          {(title || description) && (
            <div className="mb-4">
              {title && (
                <Dialog.Title className="font-serif text-lg font-semibold tracking-tight">
                  {title}
                </Dialog.Title>
              )}
              {description && (
                <Dialog.Description className="mt-1 text-sm text-ink-muted">
                  {description}
                </Dialog.Description>
              )}
            </div>
          )}
          {/* Radix requires a Title + Description (or aria-hidden alts)
              on every DialogContent. When a caller omits them, supply
              screen-reader-only labels so the a11y tree resolves. */}
          {!title && (
            <Dialog.Title className={SR_ONLY}>Dialog</Dialog.Title>
          )}
          {!description && (
            <Dialog.Description className={SR_ONLY}>
              Detail dialogu
            </Dialog.Description>
          )}
          {children}
          <Dialog.Close
            aria-label="Zavřít"
            className={cn(
              "absolute right-4 top-4 rounded-lg p-1.5",
              "text-ink-dim hover:text-ink hover:bg-surface-raised",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber",
            )}
          >
            <X size={16} />
          </Dialog.Close>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
